import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { createDemographicsT } from '../../../utils/demographicsTranslations'
import { DemographicsContent } from '../DemographicsContent'
import { type DemographicAnalysis } from './demographicsService'
import { OverviewTab } from './components/tabs/OverviewTab'
import { GrowthAnalysisTab } from './components/tabs/GrowthAnalysisTab'
import { MarketCompositionTab } from './components/tabs/MarketCompositionTab'

type LocationType = 'city' | 'district' | 'radius' | 'walk' | 'bicycle' | 'car'

export interface DemographicsSectionProps {
  entity: {
    id: string
    geo_center?: {
      lat: number
      lon: number
    }
    municipality_name?: string
    district_name?: string
  }
  userId: string
  onDataFetched: (data: DemographicAnalysis) => void
  getDemographicsData: () => DemographicAnalysis | null
  updateDemographicsData: (
    entity: any,
    userId: string,
    params: {
      locationType: LocationType
      radiusDistance: number
      walkTime: number
      bicycleTime: number
      carTime: number
    }
  ) => Promise<DemographicAnalysis | null>
  /** When true, hides filter controls and shows only the current filter value */
  readOnly?: boolean
}

export const DemographicsSection: React.FC<DemographicsSectionProps> = ({
  entity,
  userId,
  onDataFetched,
  getDemographicsData,
  updateDemographicsData,
  readOnly = false,
}) => {
  const { t: rawT, i18n } = useTranslation()
  const t = createDemographicsT(rawT, i18n)

  // Filter state
  const [activeTab, setActiveTab] = useState('overview')
  const [locationType, setLocationType] = useState<LocationType>('city')
  const [radiusDistance, setRadiusDistance] = useState(500)
  const [walkTime, setWalkTime] = useState(10)
  const [bicycleTime, setBicycleTime] = useState(15)
  const [carTime, setCarTime] = useState(10)

  // Loading and error state
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Track fetched params to prevent duplicate requests
  const paramsHashRef = useRef<string>('')

  const fetchDemographics = useCallback(async () => {
    if (!entity?.id || !entity?.geo_center || !userId) {
      return
    }

    const paramsHash = JSON.stringify({
      entityId: entity.id,
      locationType,
      radiusDistance,
      walkTime,
      bicycleTime,
      carTime,
    })

    // Skip if we already fetched with these exact params
    if (paramsHashRef.current === paramsHash) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const analysis = await updateDemographicsData(entity, userId, {
        locationType,
        radiusDistance,
        walkTime,
        bicycleTime,
        carTime,
      })

      if (analysis) {
        onDataFetched(analysis)
      }
      paramsHashRef.current = paramsHash
    } catch (err) {
      console.error('Error fetching demographics data:', err)
      setError(err instanceof Error ? err.message : t('states.fetchError'))
    } finally {
      setLoading(false)
    }
  }, [
    entity,
    userId,
    locationType,
    radiusDistance,
    walkTime,
    bicycleTime,
    carTime,
    updateDemographicsData,
    onDataFetched,
  ])

  // Fetch when filters change
  useEffect(() => {
    fetchDemographics()
  }, [fetchDemographics])

  const data = getDemographicsData()

  const renderTabContent = useCallback(() => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab data={data} t={t} />
      case 'growth':
        return <GrowthAnalysisTab data={data} t={t} />
      case 'market':
        return <MarketCompositionTab data={data} t={t} />
      default:
        return <OverviewTab data={data} t={t} />
    }
  }, [activeTab, data, t])

  const getLocationLabel = useCallback(() => {
    if (!entity) return t('filters.selectLocation')

    switch (locationType) {
      case 'city':
        return entity.municipality_name || t('filters.city')
      case 'district':
        return entity.district_name || t('filters.district')
      case 'walk':
        return t('filters.minWalk', { time: walkTime })
      case 'bicycle':
        return t('filters.minBike', { time: bicycleTime })
      case 'car':
        return t('filters.minDrive', { time: carTime })
      default:
        return t('filters.selectLocation')
    }
  }, [entity, locationType, walkTime, bicycleTime, carTime, t])

  return (
    <DemographicsContent
      data={data}
      loading={loading}
      error={error}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      locationType={locationType}
      onLocationTypeChange={setLocationType}
      radiusDistance={radiusDistance}
      onRadiusDistanceChange={setRadiusDistance}
      walkTime={walkTime}
      onWalkTimeChange={setWalkTime}
      bicycleTime={bicycleTime}
      onBicycleTimeChange={setBicycleTime}
      carTime={carTime}
      onCarTimeChange={setCarTime}
      renderTabContent={renderTabContent}
      getLocationLabel={getLocationLabel}
      cityName={entity?.municipality_name}
      districtName={entity?.district_name}
      readOnly={readOnly}
    />
  )
}
